import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users, Shield, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { MINISTRY_DEFINITIONS } from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  return { withCredentials: true };
}

function PersonCard({ person, label }) {
  return (
    <div className="border rounded p-3 bg-white">
      <p className="font-semibold text-royal-blue">
        {person.display_name || person.username || "Unnamed User"}
      </p>

      {person.username && (
        <p className="text-xs text-slate-500">@{person.username}</p>
      )}

      {(person.roles || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {(person.roles || []).map((role) => (
            <span
              key={role}
              className="text-xs px-2 py-0.5 rounded bg-royal-blue/10 text-royal-blue capitalize"
            >
              {role}
            </span>
          ))}
        </div>
      )}

      {label && (
        <p className="text-xs text-kingdom-gold mt-2 font-semibold">
          {label}
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-3"
        disabled
        title="Internal messaging will be added next."
      >
        <MessageCircle className="w-3 h-3 mr-1" />
        Message
      </Button>
    </div>
  );
}

export default function MinistryWorkspace() {
  const { ministryKey } = useParams();

  const [user, setUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [overseers, setOverseers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  const ministry = useMemo(
    () => MINISTRY_DEFINITIONS.find((m) => m.key === ministryKey),
    [ministryKey]
  );

  useEffect(() => {
    const loadWorkspace = async () => {
      setLoading(true);
      setAccessDenied(false);

      try {
        const meRes = await axios.get(`${API}/nationals/me`, getAuthHeader());
        const me = meRes.data;

        setUser(me);

        const workspaceRes = await axios.get(
          `${API}/ministries/${ministryKey}/workspace`,
          getAuthHeader()
        );

        const workspace = workspaceRes.data || {};

        setMembers(workspace.members || []);
        setOverseers(workspace.overseers || []);
        setCanManage(Boolean(workspace.can_manage));
      } catch (error) {
        console.error(
          "Ministry workspace load failed:",
          error?.response?.data || error
        );

        if (error?.response?.status === 403) {
          setAccessDenied(true);
        }

        setMembers([]);
        setOverseers([]);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspace();
  }, [ministryKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <p className="text-slate-600">Loading ministry workspace...</p>
      </div>
    );
  }

  if (!ministry) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-6">
        <Card className="card-official max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <p className="text-slate-700 mb-4">Ministry not found.</p>

            <Link
              to="/portal"
              className="text-kingdom-gold font-semibold hover:underline"
            >
              Return to Portal
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accessDenied || !user) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-6">
        <Card className="card-official max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <p className="text-slate-700 mb-4">
              You are not assigned to this ministry workspace.
            </p>

            <Link
              to="/portal"
              className="text-kingdom-gold font-semibold hover:underline"
            >
              Return to Portal
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-parchment"
      data-testid="ministry-workspace-page"
    >
      <section className="bg-royal-blue text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/portal"
            className="inline-flex items-center text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Portal
          </Link>

          <p className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
            Ministry Workspace
          </p>

          <h1 className="heading-section mt-2">{ministry.label}</h1>

          <p className="text-white/80 mt-3 max-w-3xl">
            {ministry.description}
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
          <Card className="card-official lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-royal-blue flex items-center gap-2">
                <Users className="w-5 h-5 text-kingdom-gold" />
                Ministry Members
              </CardTitle>

              <CardDescription>
                Members assigned to this ministry. Contact details are private.
              </CardDescription>
            </CardHeader>

            <CardContent>
              {members.length === 0 ? (
                <p className="text-slate-500">No members assigned yet.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {members.map((member) => (
                    <PersonCard key={member.id || member.username} person={member} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-official">
            <CardHeader>
              <CardTitle className="text-royal-blue flex items-center gap-2">
                <Shield className="w-5 h-5 text-kingdom-gold" />
                Admin Oversight
              </CardTitle>

              <CardDescription>
                Admins assigned to oversee this ministry.
              </CardDescription>
            </CardHeader>

            <CardContent>
              {overseers.length === 0 ? (
                <p className="text-slate-500">No overseers assigned yet.</p>
              ) : (
                <div className="space-y-3">
                  {overseers.map((overseer) => (
                    <PersonCard
                      key={overseer.id || overseer.username}
                      person={overseer}
                      label={overseer.admin_label || "Ministry Admin"}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-official lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-royal-blue">Workspace Tools</CardTitle>

              <CardDescription>
                Notes, files, assignments, schedules, and ministry records can be added here next.
              </CardDescription>
            </CardHeader>

            <CardContent>
              {canManage ? (
                <p className="text-slate-600">
                  You can oversee this ministry. The next build step can add member assignments,
                  posts, documents, task lists, resources, and private ministry updates.
                </p>
              ) : (
                <p className="text-slate-600">
                  This workspace lets assigned members see sanitized ministry members and admins.
                  Internal messaging and private updates can be added next.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}